import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Alert, Space, Typography, Checkbox, Divider } from 'antd';
import { SaveOutlined, CloseOutlined, DeleteOutlined, EditOutlined, SafetyOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import './FirmaPaciente.css';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

/**
 * Componente Modal para capturar la firma del paciente/acompañante
 * Optimizado para dispositivos móviles con soporte landscape
 * Se usa al finalizar un traslado
 *
 * @param {object} trasladoData - Datos del traslado para autocompletar
 */
const FirmaPaciente = ({ visible, onCancel, onSubmit, loading = false, trasladoData = null }) => {
  const [form] = Form.useForm();
  const [esAcompanante, setEsAcompanante] = useState(false);
  const [mostrarFirma, setMostrarFirma] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [formValues, setFormValues] = useState(null); // Guardar valores del formulario
  const sigCanvas = useRef(null);

  // Estados para el paso de términos y condiciones
  const [mostrarTerminos, setMostrarTerminos] = useState(true); // Mostrar términos primero
  const [aceptadoPrivacidad, setAceptadoPrivacidad] = useState(false);
  const [aceptadoTerminos, setAceptadoTerminos] = useState(false);

  // Detectar orientación del dispositivo
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeMode = window.innerHeight < window.innerWidth;
      setIsLandscape(isLandscapeMode);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Resetear estado cuando se abre el modal
  useEffect(() => {
    if (visible) {
      setMostrarTerminos(true);
      setAceptadoPrivacidad(false);
      setAceptadoTerminos(false);
    }
  }, [visible]);

  // Autocompletar datos del paciente cuando se abre el modal
  useEffect(() => {
    if (visible && trasladoData) {
      const initialValues = {
        tipo_firma: 'paciente',
        tipo_documento: trasladoData.tipo_documento_paciente || 'Cédula de Ciudadanía',
      };

      // Autocompletar nombre del paciente
      if (trasladoData.nombre_paciente) {
        initialValues.nombre = trasladoData.nombre_paciente;
      }

      // Autocompletar número de documento del paciente
      if (trasladoData.numero_documento_paciente) {
        initialValues.numero_documento = trasladoData.numero_documento_paciente;
      }

      form.setFieldsValue(initialValues);
      console.log('=== DATOS AUTOCOMPLETADOS ===');
      console.log('Nombre paciente:', trasladoData.nombre_paciente);
      console.log('Tipo documento:', trasladoData.tipo_documento_paciente);
      console.log('Número documento:', trasladoData.numero_documento_paciente);
    }
  }, [visible, trasladoData, form]);

  const handleAceptarTerminos = () => {
    if (!aceptadoPrivacidad || !aceptadoTerminos) {
      message.warning('Debe aceptar tanto la Política de Privacidad como los Términos y Condiciones para continuar');
      return;
    }
    setMostrarTerminos(false);
    message.success('Términos aceptados. Ahora puede continuar con el registro de la firma');
  };

  const handleTipoFirmaChange = (value) => {
    setEsAcompanante(value === 'acompanante');

    if (value === 'paciente') {
      // Si vuelve a seleccionar paciente, restaurar datos del paciente
      form.setFieldsValue({
        parentesco: null,
        nombre: trasladoData?.nombre_paciente || '',
        tipo_documento: trasladoData?.tipo_documento_paciente || 'Cédula de Ciudadanía',
        numero_documento: trasladoData?.numero_documento_paciente || ''
      });
    } else if (value === 'acompanante' && trasladoData) {
      // Si selecciona acompañante, cargar datos del responsable/acompañante
      console.log('=== DEBUG: trasladoData completo ===');
      console.log('trasladoData:', trasladoData);
      console.log('nombre_responsable en trasladoData:', trasladoData.nombre_responsable);

      const acompananteData = {
        parentesco: trasladoData.parentesco_responsable || null,
        nombre: trasladoData.nombre_responsable || '',
        numero_documento: trasladoData.numero_contacto_responsable || '',
        tipo_documento: 'Cédula de Ciudadanía' // Valor por defecto
      };

      form.setFieldsValue(acompananteData);

      console.log('=== DATOS ACOMPAÑANTE CARGADOS ===');
      console.log('Nombre:', trasladoData.nombre_responsable);
      console.log('Parentesco:', trasladoData.parentesco_responsable);
      console.log('Número contacto:', trasladoData.numero_contacto_responsable);
    }
  };

  const limpiarFirma = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const limpiarDatosAcompanante = () => {
    Modal.confirm({
      title: '¿Limpiar datos del acompañante?',
      content: 'Se borrarán todos los datos precargados. Podrá ingresar nuevos datos.',
      okText: 'Sí, limpiar',
      cancelText: 'Cancelar',
      onOk: () => {
        form.setFieldsValue({
          nombre: '',
          tipo_documento: 'Cédula de Ciudadanía',
          numero_documento: '',
          parentesco: null
        });
        message.success('Datos limpiados correctamente');
      }
    });
  };

  const abrirPantallaFirma = async () => {
    try {
      // Validar y guardar campos del formulario
      const values = await form.validateFields();

      console.log("=== VALORES DEL FORMULARIO (guardados) ===");
      console.log("Values:", values);

      // Guardar en el estado para usar después
      setFormValues(values);

      // Abrir pantalla de firma
      setMostrarFirma(true);
    } catch (error) {
      message.warning('Por favor complete todos los campos antes de firmar');
    }
  };

  const cerrarPantallaFirma = () => {
    setMostrarFirma(false);
  };

  const handleSubmit = async () => {
    try {
      // Usar los valores guardados del formulario
      if (!formValues) {
        message.error('Error: No se encontraron los datos del formulario');
        return;
      }

      console.log("=== VALORES DEL FORMULARIO (recuperados) ===");
      console.log("Values:", formValues);

      // Validar que haya firma
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        message.warning('Por favor, capture la firma antes de continuar');
        return;
      }

      // Obtener la firma como base64
      const firmaBase64 = sigCanvas.current.toDataURL('image/png');

      // Preparar datos para enviar usando los valores guardados
      const firmaData = {
        tipo_firma: formValues.tipo_firma,
        nombre: formValues.nombre,
        tipo_documento: formValues.tipo_documento,
        numero_documento: formValues.numero_documento,
        parentesco: formValues.tipo_firma === 'acompanante' ? formValues.parentesco : null,
        observaciones: formValues.observaciones || null,
        firma_imagen: firmaBase64
      };

      console.log("=== DATOS DE FIRMA PREPARADOS ===");
      console.log("Tipo firma:", firmaData.tipo_firma);
      console.log("Nombre:", firmaData.nombre);
      console.log("Tipo documento:", firmaData.tipo_documento);
      console.log("Numero documento:", firmaData.numero_documento);
      console.log("Parentesco:", firmaData.parentesco);
      console.log("Firma imagen (primeros 100 chars):", firmaData.firma_imagen?.substring(0, 100));

      // Llamar al callback con los datos
      onSubmit(firmaData);

      // Limpiar formulario y firma
      form.resetFields();
      limpiarFirma();
      setEsAcompanante(false);
      setMostrarFirma(false);
      setFormValues(null);
      setMostrarTerminos(true);
      setAceptadoPrivacidad(false);
      setAceptadoTerminos(false);

    } catch (error) {
      console.error('Error al validar formulario:', error);
    }
  };

  const handleCancel = () => {
    // Confirmar cancelación si hay datos
    if (!sigCanvas.current?.isEmpty() || form.isFieldsTouched() || formValues || !mostrarTerminos) {
      Modal.confirm({
        title: '¿Cancelar captura de firma?',
        content: 'Se perderán los datos ingresados y deberá aceptar nuevamente los términos. ¿Deseas continuar?',
        okText: 'Sí, cancelar',
        cancelText: 'No, volver',
        onOk: () => {
          form.resetFields();
          limpiarFirma();
          setEsAcompanante(false);
          setMostrarFirma(false);
          setFormValues(null);
          setMostrarTerminos(true);
          setAceptadoPrivacidad(false);
          setAceptadoTerminos(false);
          onCancel();
        }
      });
    } else {
      onCancel();
    }
  };

  // Renderizar pantalla de términos y condiciones (PASO 1)
  if (visible && mostrarTerminos) {
    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '18px' }}>
            <SafetyOutlined style={{ fontSize: 24, color: '#52c41a' }} />
            <span>Autorización de Tratamiento de Datos</span>
          </div>
        }
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={window.innerWidth > 768 ? 800 : '95%'}
        maskClosable={false}
        destroyOnClose
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px 8px' }}>
          <Alert
            message="Lectura Obligatoria"
            description="Antes de registrar la firma, la persona que firmará debe leer y aceptar la Política de Privacidad y los Términos y Condiciones."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* POLÍTICA DE PRIVACIDAD */}
            <div style={{
              border: '2px solid #52c41a',
              borderRadius: '8px',
              padding: '16px',
              background: aceptadoPrivacidad ? '#f6ffed' : '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <SafetyOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                <Title level={5} style={{ margin: 0 }}>Política de Privacidad y Tratamiento de Datos</Title>
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 16, padding: '8px' }}>
                <Paragraph>
                  <Text strong>MOGOTAX SAS</Text> requiere su consentimiento para el tratamiento de datos personales
                  relacionados con este traslado médico.
                </Paragraph>

                <Title level={5}>Datos que recopilamos:</Title>
                <ul>
                  <li><strong>Datos de identificación:</strong> Nombre completo, tipo y número de documento</li>
                  <li><strong>Datos de salud:</strong> Información relacionada con el traslado médico</li>
                  <li><strong>Firma digital:</strong> Su firma para validación del servicio prestado</li>
                  <li><strong>Parentesco:</strong> En caso de acompañantes</li>
                </ul>

                <Title level={5}>Finalidad del tratamiento:</Title>
                <ul>
                  <li>Generar documentación legal del traslado realizado</li>
                  <li>Validar la prestación del servicio de transporte sanitario</li>
                  <li>Cumplir con obligaciones legales y regulatorias del sector salud</li>
                  <li>Realizar auditorías de calidad del servicio</li>
                  <li>Procesar facturación y pagos con entidades de salud</li>
                </ul>

                <Title level={5}>Compartir datos con terceros:</Title>
                <Paragraph>
                  Sus datos pueden ser compartidos con:
                </Paragraph>
                <ul>
                  <li><strong>Entidades de salud:</strong> Para coordinación y facturación de traslados</li>
                  <li><strong>Autoridades competentes:</strong> Cuando sea requerido por ley</li>
                  <li><strong>Aseguradoras:</strong> En caso de incidentes que requieran activación de pólizas</li>
                </ul>

                <Title level={5}>Sus derechos como titular:</Title>
                <Paragraph>
                  De acuerdo con la Ley 1581 de 2012, usted tiene derecho a conocer, actualizar,
                  rectificar y suprimir sus datos personales, así como revocar su consentimiento.
                </Paragraph>
                <Paragraph>
                  <strong>Contacto:</strong> protecciondatos@mogotaxsas.com
                </Paragraph>

                <Title level={5}>Tiempo de conservación:</Title>
                <Paragraph>
                  Sus datos serán conservados durante el tiempo establecido por la normativa legal
                  para documentos relacionados con servicios de salud (mínimo 10 años).
                </Paragraph>
              </div>

              <Checkbox
                checked={aceptadoPrivacidad}
                onChange={(e) => setAceptadoPrivacidad(e.target.checked)}
                style={{ fontSize: '14px' }}
              >
                <strong>He leído y acepto la Política de Privacidad y Tratamiento de Datos Personales de MOGOTAX</strong>
              </Checkbox>
            </div>

            <Divider />

            {/* TÉRMINOS Y CONDICIONES */}
            <div style={{
              border: '2px solid #1890ff',
              borderRadius: '8px',
              padding: '16px',
              background: aceptadoTerminos ? '#e6f7ff' : '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <FileTextOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                <Title level={5} style={{ margin: 0 }}>Términos y Condiciones del Servicio</Title>
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: 16, padding: '8px' }}>
                <Title level={5}>Validación del Servicio:</Title>
                <Paragraph>
                  Al firmar este documento, usted certifica que:
                </Paragraph>
                <ul>
                  <li>El traslado se realizó conforme a lo solicitado</li>
                  <li>El paciente fue tratado con respeto y profesionalismo</li>
                  <li>El vehículo se encontraba en condiciones adecuadas</li>
                  <li>Los datos registrados en el traslado son correctos</li>
                </ul>

                <Title level={5}>Firma Digital:</Title>
                <Paragraph>
                  La firma digital capturada tiene el mismo valor legal que una firma manuscrita
                  y será utilizada para generar el control de servicio y documentación legal del traslado.
                </Paragraph>

                <Title level={5}>Observaciones:</Title>
                <Paragraph>
                  Si existe alguna novedad, incidente o situación especial durante el traslado,
                  debe registrarla en el campo de observaciones antes de firmar.
                </Paragraph>

                <Title level={5}>Responsabilidad:</Title>
                <Paragraph>
                  Al firmar, usted acepta que la información proporcionada es veraz y que el
                  servicio de traslado se prestó de acuerdo a las condiciones pactadas.
                </Paragraph>

                <Title level={5}>Derechos del Usuario:</Title>
                <Paragraph>
                  Tiene derecho a solicitar copia del control de servicio firmado y a presentar
                  quejas o reclamos si considera que el servicio no fue prestado adecuadamente.
                </Paragraph>
                <Paragraph>
                  <strong>Contacto:</strong> soporte@mogotaxsas.com
                </Paragraph>
              </div>

              <Checkbox
                checked={aceptadoTerminos}
                onChange={(e) => setAceptadoTerminos(e.target.checked)}
                style={{ fontSize: '14px' }}
              >
                <strong>He leído y acepto los Términos y Condiciones del Servicio de MOGOTAX</strong>
              </Checkbox>
            </div>

            <Divider />

            {/* Botón de Continuar */}
            <div style={{ textAlign: 'center', paddingTop: 16 }}>
              <Text type="secondary" italic style={{ display: 'block', marginBottom: 16 }}>
                Al hacer clic en "Aceptar y Continuar", autorizo expresamente a MOGOTAX SAS
                para el tratamiento de mis datos personales conforme a lo establecido anteriormente.
              </Text>
              <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button
                  onClick={handleCancel}
                  size="large"
                  icon={<CloseOutlined />}
                >
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleAceptarTerminos}
                  disabled={!aceptadoPrivacidad || !aceptadoTerminos}
                  style={{ minWidth: '200px' }}
                >
                  Aceptar y Continuar
                </Button>
              </Space>
            </div>
          </Space>
        </div>
      </Modal>
    );
  }

  // Renderizar pantalla de firma fullscreen en modo landscape
  if (mostrarFirma) {
    return (
      <Modal
        title={null}
        open={visible && mostrarFirma}
        onCancel={cerrarPantallaFirma}
        footer={null}
        width="100vw"
        style={{
          top: 0,
          padding: 0,
          maxWidth: '100vw'
        }}
        bodyStyle={{
          height: '100vh',
          padding: 0,
          background: '#f0f2f5',
          display: 'flex',
          flexDirection: 'column'
        }}
        maskClosable={false}
        destroyOnClose={false}
        className="firma-fullscreen-modal"
      >
        <div className="firma-fullscreen-container">
          {/* Header con instrucciones */}
          <div className="firma-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '16px', color: '#fff' }}>
                Capture la Firma
              </Text>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={cerrarPantallaFirma}
                size="large"
                style={{ color: '#fff' }}
              />
            </div>
          </div>

          {/* Canvas de firma - tamaño ajustado dinámicamente */}
          <div className="firma-canvas-wrapper">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: 'signature-canvas-fullscreen',
                width: Math.min(window.innerWidth - 40, 800),
                height: isLandscape ? window.innerHeight - 200 : 300
              }}
              backgroundColor="#ffffff"
              penColor="#000000"
            />
          </div>

          {/* Footer con botones de acción */}
          <div className="firma-footer">
            <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                type="default"
                icon={<DeleteOutlined />}
                onClick={limpiarFirma}
                size="large"
                danger
              >
                Limpiar
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={loading}
                size="large"
                style={{ minWidth: '150px' }}
              >
                Guardar y Finalizar
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
    );
  }

  // Renderizar formulario de datos
  return (
    <Modal
      title="Datos del Paciente/Acompañante"
      open={visible && !mostrarFirma}
      onCancel={handleCancel}
      footer={null}
      width={window.innerWidth > 768 ? 700 : '95%'}
      maskClosable={false}
      destroyOnClose
    >
      <Alert
        message="Información Importante"
        description="Complete los datos del paciente o acompañante que firmará. Luego presione el botón para capturar la firma."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          tipo_firma: 'paciente',
          tipo_documento: 'Cédula de Ciudadanía'
        }}
      >
        <Form.Item
          label="¿Quién firma?"
          name="tipo_firma"
          rules={[{ required: true, message: 'Este campo es requerido' }]}
        >
          <Select onChange={handleTipoFirmaChange} size="large">
            <Option value="paciente">Paciente</Option>
            <Option value="acompanante">Acompañante</Option>
          </Select>
        </Form.Item>

        {esAcompanante && (
          <Alert
            message="Datos del Acompañante Precargados"
            description="Si el acompañante es diferente, puede limpiar los datos y escribir nuevos."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button
                size="small"
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={limpiarDatosAcompanante}
              >
                Limpiar Datos
              </Button>
            }
          />
        )}

        <Form.Item
          label="Nombre Completo"
          name="nombre"
          rules={[
            { required: true, message: 'Por favor ingrese el nombre completo' },
            { min: 3, message: 'El nombre debe tener al menos 3 caracteres' }
          ]}
        >
          <Input
            placeholder="Nombre completo"
            size="large"
            allowClear={esAcompanante}
          />
        </Form.Item>

        <Form.Item
          label="Tipo de Documento"
          name="tipo_documento"
          rules={[{ required: true, message: 'Este campo es requerido' }]}
        >
          <Select size="large" showSearch optionFilterProp="children">
            <Option value="Adulto sin Identificación">Adulto sin Identificación</Option>
            <Option value="Cédula de Ciudadanía">Cédula de Ciudadanía</Option>
            <Option value="Cédula de Extranjería">Cédula de Extranjería</Option>
            <Option value="NIT">NIT</Option>
            <Option value="Menor sin Identificación">Menor sin Identificación</Option>
            <Option value="Pasaporte">Pasaporte</Option>
            <Option value="Permiso Especial Permanente">Permiso Especial Permanente</Option>
            <Option value="Permiso por Protección Temporal">Permiso por Protección Temporal</Option>
            <Option value="Registro Civil">Registro Civil</Option>
            <Option value="Salvoconducto">Salvoconducto</Option>
            <Option value="Tarjeta de Identidad">Tarjeta de Identidad</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Número de Documento"
          name="numero_documento"
          rules={[
            { required: true, message: 'Por favor ingrese el número de documento' },
            { pattern: /^[0-9]+$/, message: 'Solo se permiten números' },
            { min: 5, message: 'El documento debe tener al menos 5 dígitos' }
          ]}
        >
          <Input
            placeholder="Número de documento"
            size="large"
            maxLength={15}
            allowClear={esAcompanante}
          />
        </Form.Item>

        {esAcompanante && (
          <Form.Item
            label="Parentesco con el Paciente"
            name="parentesco"
            rules={[
              { required: true, message: 'Por favor especifique el parentesco' }
            ]}
          >
            <Select
              placeholder="Seleccione el parentesco"
              size="large"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              <Option value="Padre">Padre</Option>
              <Option value="Madre">Madre</Option>
              <Option value="Hijo(a)">Hijo(a)</Option>
              <Option value="Abuelo(a)">Abuelo(a)</Option>
              <Option value="Hermano(a)">Hermano(a)</Option>
              <Option value="Tío(a)">Tío(a)</Option>
              <Option value="Sobrino(a)">Sobrino(a)</Option>
              <Option value="Nieto(a)">Nieto(a)</Option>
              <Option value="Primo(a)">Primo(a)</Option>
              <Option value="Cónyuge (Esposo/a)">Cónyuge (Esposo/a)</Option>
              <Option value="Pareja">Pareja</Option>
              <Option value="Cuñado(a)">Cuñado(a)</Option>
              <Option value="Suegro(a)">Suegro(a)</Option>
              <Option value="Yerno">Yerno</Option>
              <Option value="Nuera">Nuera</Option>
              <Option value="Padrastro">Padrastro</Option>
              <Option value="Madrastra">Madrastra</Option>
              <Option value="Hijastro(a)">Hijastro(a)</Option>
              <Option value="Tutor(a) legal">Tutor(a) legal</Option>
              <Option value="Representante legal">Representante legal</Option>
              <Option value="Amigo(a)">Amigo(a)</Option>
              <Option value="Vecino(a)">Vecino(a)</Option>
              <Option value="Compañero(a) de trabajo">Compañero(a) de trabajo</Option>
              <Option value="Cuidador(a)">Cuidador(a)</Option>
            </Select>
          </Form.Item>
        )}

        <Form.Item
          label="Observaciones del Traslado (Opcional)"
          name="observaciones"
          rules={[
            { max: 500, message: 'Las observaciones no pueden exceder 500 caracteres' }
          ]}
        >
          <Input.TextArea
            placeholder="Escriba aquí cualquier observación sobre el traslado: novedades, incidentes, cambios de ruta, etc."
            rows={4}
            size="large"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }} size="middle">
            <Button
              onClick={handleCancel}
              size="large"
              icon={<CloseOutlined />}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={abrirPantallaFirma}
              size="large"
              icon={<EditOutlined />}
              style={{ minWidth: '200px' }}
            >
              Continuar a Firma
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FirmaPaciente;
